import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { formatCalendarDate } from '../lib/calendar-date';
import { api } from '../api/client';
import type { TimeOffOverlap } from '../types/time-off-overlap';

/**
 * Who else is already off in the dates a hand is about to ask for.
 *
 * Sits under the date pickers on the time-off request form, so he knows he is
 * leaving the crew short BEFORE he submits rather than when the office says no.
 * On TNT, 4 of 11 requests were submitted into a window that already held an
 * approved colleague (36%); every other tenant is zero.
 *
 * NOT A CALENDAR GRID. The question is "is anyone else out on these days", not
 * "show me the month" — and a month grid on a phone is the wrong shape for
 * both. A short list under the pickers answers it in one glance.
 *
 * ONE COMPONENT, TWO SCREENS. FieldTimeOffScreen (casing hand) and
 * DriverTimeOffScreen (trucking) are the same form for two verticals and are
 * already a near-copy of each other, differing only in chrome. Shipping this as
 * two copies of the same block is how the two screens start to diverge for no
 * reason, so it is shipped as one.
 *
 * WHAT THE PAYLOAD DELIBERATELY LACKS. `GET /employee/time-off/overlaps`
 * returns five columns — user_id, first_name, last_name, start_date, end_date.
 * No type, no reason, no status. A hand may know a colleague is away and for
 * how long; why they are away is between that colleague and the office. This
 * component could not render it if it wanted to, which is the point — the
 * office's own `GET /dispatch/time-off/calendar` returns `time_off_type` and
 * was deliberately not reused.
 */

/**
 * Time-off bounds are calendar days. @see formatCalendarDate.
 *
 * `new Date('2026-08-25')` is UTC midnight, which in Alberta is 18:00 on the
 * 24th — so the naive expression renders a colleague's leave starting a day
 * early, in the direction that makes the crew look thinner than it is. The
 * falsification for this line is GREEN under TZ=UTC and red under
 * TZ=America/Edmonton, which is the whole reason CI runs the suite in both.
 */
const formatDay = (value: string | null | undefined): string =>
  formatCalendarDate(value, { month: 'short', day: 'numeric' }) ?? '';

/**
 * An approved absence can outlive the `users` row it points at — the API's
 * LEFT JOIN keeps it rather than dropping it, because a dropped entry makes the
 * window read as COVERED when nobody is working it. A name is not invented
 * here: "A crew member" is visibly a gap, where a guessed name would be a
 * confident wrong answer.
 *
 * WHICH EXISTING FALLBACK THIS MATCHES, because the brief that asked for it said
 * "matching the three existing consumers" and that is not what is in the repo.
 * Measured 2026-09-11: no time-off consumer uses this string. `TimeOffScreen.tsx`
 * resolves a name three times and falls back three DIFFERENT ways —
 * `|| 'Unknown'`, `|| ''`, `|| 'Employee'`. The one real precedent for
 * "A crew member" is `src/lib/call-sheet-send-result.ts`, which is also
 * crew-facing, and that is the right neighbour: `Unknown` and `Employee` are
 * office vocabulary for an office list, and this is a hand reading about his
 * crew. So the string stands; the justification for it was wrong, and the three
 * office sites disagreeing with each other is its own small finding — see
 * fe#639.
 */
export function overlapName(row: TimeOffOverlap): string {
  const name = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();
  return name || 'A crew member';
}

interface Props {
  startDate: string;
  endDate: string;
}

export default function TimeOffOverlaps({ startDate, endDate }: Props) {
  const enabled = Boolean(startDate && endDate);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['field-time-off-overlaps', startDate, endDate],
    queryFn: () =>
      api.employee
        .timeOffOverlaps(startDate, endDate)
        .then((r: any) => r.data?.data ?? r.data ?? []),
    enabled,
  });

  // Nothing at all until BOTH dates are set. A half-filled range is not a
  // question this endpoint can answer, and the API 400s on it.
  if (!enabled) return null;

  if (isLoading) {
    return (
      <p className="text-xs text-fieldiq-muted" data-testid="overlaps-loading">
        Checking who else is off…
      </p>
    );
  }

  // A refusal is not "nobody is off". Rendering the empty state here would tell
  // a hand the crew is covered on the strength of a request that never
  // answered — the silent-blank-field family, in the direction that reads as
  // data. Say the check did not run and let him ask dispatch.
  if (isError) {
    return (
      <p className="text-xs text-amber-400" data-testid="overlaps-error">
        Couldn't check who else is off. Ask dispatch before you submit.
      </p>
    );
  }

  const rows: TimeOffOverlap[] = Array.isArray(data) ? data : [];

  if (rows.length === 0) {
    return (
      <p className="text-xs text-fieldiq-muted" data-testid="overlaps-empty">
        Nobody else is off in these dates.
      </p>
    );
  }

  return (
    <div data-testid="overlaps-list" className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-fieldiq-text">
        <Users size={12} className="text-fieldiq-accent" />
        <span>Already off in these dates</span>
      </div>
      <ul className="space-y-0.5">
        {rows.map((row, i) => (
          <li
            key={`${row.user_id ?? 'orphan'}-${row.start_date}-${row.end_date}-${i}`}
            className="text-xs text-fieldiq-muted"
          >
            {overlapName(row)} · {formatDay(row.start_date)} – {formatDay(row.end_date)}
          </li>
        ))}
      </ul>
    </div>
  );
}
